import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { ApiError, NetworkError } from "../../api/errors";
import { myRosterSchema, rosterQueryKeys } from "../../api/rosterSchemas";
import type { MyRoster } from "../../api/rosterSchemas";
import type { LocalStore } from "../../offline/LocalStore";
import { useOffline } from "../../offline/OfflineContext";
import { useAuth } from "../../shared/auth/AuthContext";
import { useOwnerKey } from "../../shared/session/tenantSelection";
import { useMyRostering } from "./useRostering";

/** „Mein Dienstplan“ with the time it was fetched. */
export interface MyRosterResult {
  readonly roster: MyRoster;
  /** Device time when the roster was fetched, in milliseconds. */
  readonly fetchedAtMs: number;
  /** Whether the server could not be reached and the last known roster on the device is shown. */
  readonly fromDevice: boolean;
}

/** Who the kept roster belongs to: the owner key of the session and the signed-in account. */
interface KeptFor {
  readonly owner: string;
  readonly subject: string;
}

/**
 * Returns the roster without the shifts of colleagues: only own and open shifts are kept on the
 * device (data minimization, ADR-0150).
 *
 * @param roster - The roster as fetched.
 * @returns The roster to keep.
 */
export function keptRoster(roster: MyRoster): MyRoster {
  const kept: MyRoster = {
    ...roster,
    shifts: roster.shifts.filter((shift) => shift.kind !== "Colleague"),
  };

  return kept;
}

/**
 * Returns the last known roster, or `null` if none is kept or it no longer fits the schema; a kept
 * roster is a convenience, so a storage that fails reads as none.
 *
 * @param store - The local store.
 * @param keptFor - Owner of the roster.
 * @returns The kept roster or `null`.
 */
async function readKept(store: LocalStore, keptFor: KeptFor): Promise<MyRosterResult | null> {
  const cached = await store.getRosterCache(keptFor.owner).catch(() => null);
  const parsed = cached === null ? null : myRosterSchema.safeParse(cached.roster);
  const kept: MyRosterResult | null =
    cached === null || !parsed?.success
      ? null
      : { roster: parsed.data, fetchedAtMs: cached.fetchedAtMs, fromDevice: true };

  return kept;
}

/** Error codes that say rostering is off or not in the plan: the kept roster is deleted then. */
const switchedOffCodes: readonly string[] = ["TT-3270", "TT-3034"];

/** „Mein Dienstplan“ with the state of rostering. */
export interface MyRosterState {
  /** The query of the roster; it does not load while rostering is off. */
  readonly query: UseQueryResult<MyRosterResult>;
  /** Whether the roster is loaded: rostering is active, or its state is unknown without connection. */
  readonly enabled: boolean;
  /** Whether the server said that rostering is off. */
  readonly switchedOff: boolean;
}

/**
 * Loads „Mein Dienstplan“ of the next four weeks (`GET /me/roster`, T-178): the own published shifts
 * and the open shifts of the person's rosters, colleagues only with names after the tenant switch.
 * It loads while `GET /me/rostering` says rostering is active, and also while that state is unknown
 * without connection (failed or paused request), so the kept roster is shown offline. Own and open
 * shifts are kept on the device under the owner key of the session with the time they were fetched;
 * they are deleted on sign-out, for other accounts, and once the server says rostering is off
 * (`active: false`, TT-3270 or TT-3034).
 *
 * @returns The query with the state of rostering.
 */
export function useMyRoster(): MyRosterState {
  const client = useApiClient();
  const offline = useOffline();
  const { store } = offline;
  const owner = useOwnerKey();
  const { user } = useAuth();
  const rostering = useMyRostering(true);
  const unknownOffline =
    rostering.data === undefined &&
    (rostering.error instanceof NetworkError || rostering.fetchStatus === "paused");
  const switchedOff = rostering.data?.active === false;
  const enabled = rostering.data?.active === true || unknownOffline;
  const keptFor: KeptFor | null =
    owner !== null && user !== null ? { owner, subject: user.subject } : null;

  useEffect(() => {
    if (switchedOff && owner !== null) {
      // Deleting the kept roster is best effort; a blocked storage is cleared on sign-out.
      store.deleteRosterCache(owner).catch(() => undefined);
    }
  }, [switchedOff, owner, store]);

  const query = useQuery({
    queryKey: rosterQueryKeys.myRoster(owner ?? ""),
    queryFn: async ({ signal }): Promise<MyRosterResult> => {
      try {
        const roster = await client.get("/api/v1/me/roster", myRosterSchema, { signal });
        const fetchedAtMs = offline.nowMs();

        if (keptFor !== null) {
          // Keeping the roster is a convenience; a full or blocked storage must not fail the page.
          await offline.store
            .putRosterCache({
              ownerSubject: keptFor.owner,
              subject: keptFor.subject,
              fetchedAtMs,
              roster: keptRoster(roster),
            })
            .catch(() => undefined);
        }

        const fetched: MyRosterResult = { roster, fetchedAtMs, fromDevice: false };
        return fetched;
      } catch (error) {
        if (
          keptFor !== null &&
          error instanceof ApiError &&
          error.code !== null &&
          switchedOffCodes.includes(error.code)
        ) {
          await offline.store.deleteRosterCache(keptFor.owner).catch(() => undefined);
        }

        const kept =
          keptFor !== null && error instanceof NetworkError
            ? await readKept(offline.store, keptFor)
            : null;

        if (kept === null) {
          throw error;
        }

        return kept;
      }
    },
    // The query runs without connection too, so that it can fall back to the kept roster.
    networkMode: "always",
    enabled,
    staleTime: 60_000,
  });
  const state: MyRosterState = { query, enabled, switchedOff };

  return state;
}
